// TODO: Add Types support using Generics
// TODO: Request level log toggle
// TODO: Have own config, do not extend axios
// TODO: Constructor should take anything that extends our own config

import axios, { type AxiosInstance, type AxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { type ZodIssue, ZodSchema } from 'zod';

import { AppError } from './app-error';
import { convertToCamelCase, convertToSnakeCase } from './case-converter';

export type TNetworkDiagnostics = {
  online?: boolean;
  connectionType?: string;
  requestDurationMs?: number;
  errorCode?: string;
  requestUrl?: string;
  requestMethod?: string;
};

/**
 * Custom error class for API errors.
 * Includes additional context such as HTTP status code and error data.
 */
type TErrorData = {
  error?: string;
  message?: string;
  errorCode?: string;
  code?: string;
  provider?: string;
  downtimeEnd?: string;
};

export class ApiError extends AppError {
  public statusCode?: number;
  public data?: TErrorData;
  public code?: string;
  public validationIssues?: ZodIssue[];
  public networkDiagnostics?: TNetworkDiagnostics;

  constructor(message: string, statusCode?: number, data?: TErrorData, validationIssues?: ZodIssue[]) {
    super(message, { context: { statusCode, data, validationIssues } });
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.data = data;
    this.code = data?.code;
    this.validationIssues = validationIssues;
  }

  get isNetworkError() {
    return this.statusCode === undefined && this.networkDiagnostics?.errorCode === 'ERR_NETWORK';
  }

  get isTransientNetworkError() {
    return this.isNetworkError || (this.statusCode === undefined && this.networkDiagnostics?.errorCode === 'ECONNABORTED');
  }
}

export type THttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * A type alias for a custom error handler function.
 */
type TErrorHandler = (error: AxiosError) => void;

type TZodSchemaParam = ZodSchema | undefined | null;

/**
 * Extended interface for custom configuration options.
 */
export interface IApiClientConfig extends AxiosRequestConfig {
  enableCaseConversion?: boolean;
  getAuthToken?: () => string | null;
}

export type TCustomErrorHandlers = { [statusCode: number]: TErrorHandler };

/**
 * ApiClient provides a unified interface over the axios HTTP client
 * It centralizes error handling, supports custom error handlers
 * Enables optional response validation using Zod
 * Optionally converts request and response payloads between camelCase and snake_case
 * Logs request and response data for debugging
 */
class ApiClient {
  protected axiosInstance: AxiosInstance;
  protected enableCaseConversion: boolean;
  private customErrorHandlers: TCustomErrorHandlers;
  private getAuthToken?: () => string | null;

  constructor(config: IApiClientConfig = {}, customErrorHandlers: TCustomErrorHandlers = {}) {
    this.axiosInstance = axios.create(config);
    this.customErrorHandlers = customErrorHandlers;
    this.enableCaseConversion = config.enableCaseConversion || false;
    this.getAuthToken = config.getAuthToken;

    // Request interceptor: stamp start time, and attach the bearer token when getAuthToken is configured.
    this.axiosInstance.interceptors.request.use(
      (request) => {
        (request as AxiosRequestConfig & { _startTime?: number })._startTime = Date.now();
        const token = this.getAuthToken?.();
        if (token) {
          // A per-request Authorization header wins — callers that pass one explicitly mean it,
          // and silently swapping in the ambient session token would send the wrong credential.
          const headers = AxiosHeaders.from(request.headers ?? {});
          if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
          request.headers = headers;
        }
        return request;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor for centralized error handling and logging in development
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // if (import.meta.env.DEV) console.debug(`[ApiClient] Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        const diagnostics = this.captureNetworkDiagnostics(error);

        if (error.response) {
          const statusCode = error.response.status;
          const rawErrorData = error.response.data;
          const errorData = (
            this.enableCaseConversion && typeof rawErrorData === 'object' && rawErrorData !== null ? convertToCamelCase(rawErrorData) : rawErrorData
          ) as TErrorData | undefined;
          const message = errorData?.message || errorData?.error || `Request failed with status ${statusCode}`;

          // Invoke custom error handler for the status code, if available.
          if (this.customErrorHandlers[statusCode]) this.customErrorHandlers[statusCode](error);

          const apiError = new ApiError(message, statusCode, errorData);
          apiError.networkDiagnostics = diagnostics;
          return Promise.reject(apiError);
        }

        // No response — network error, timeout, cancelled request, etc.
        const message = error instanceof Error ? error.message : 'Network error';
        const apiError = new ApiError(message);
        apiError.networkDiagnostics = diagnostics;
        return Promise.reject(apiError);
      },
    );
  }

  private captureNetworkDiagnostics(error: AxiosError): TNetworkDiagnostics {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const conn = nav && 'connection' in nav ? (nav as { connection?: { effectiveType?: string } }).connection : undefined;
    const startTime = (error.config as (AxiosRequestConfig & { _startTime?: number }) | undefined)?._startTime;

    return {
      online: nav?.onLine,
      connectionType: conn?.effectiveType,
      requestDurationMs: startTime ? Date.now() - startTime : undefined,
      errorCode: error.code,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method?.toUpperCase(),
    };
  }

  /**
   * Validates the response data against an optional Zod schema.
   * @param data - The response data.
   * @param schema - Optional Zod schema for validation.
   * @param status - HTTP status code for context.
   * @returns Validated data.
   * @throws ApiError if validation fails.
   */
  private validateResponse(data: TErrorData, schema: ZodSchema) {
    const result = schema.safeParse(data);
    if (!result.success) throw new ApiError('Something went wrong while processing your request', 500, data, result.error.issues);
    return result.data;
  }

  /**
   * General method for making API requests.
   * @param method - HTTP method ('get', 'post', 'put', 'delete').
   * @param url - Endpoint URL.
   * @param data - Request payload (if applicable).
   * @param config - Additional Axios configuration.
   * @param schema - Optional Zod schema for response validation.
   * @returns The validated response data.
   */
  async request(method: THttpMethod, url: string, body?: unknown, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    try {
      if (this.enableCaseConversion) {
        if (body && typeof body === 'object' && !(body instanceof FormData)) body = convertToSnakeCase(body);
        if (config.params && typeof config.params === 'object') config.params = convertToSnakeCase(config.params);
      }
      let { data } = await this.axiosInstance.request({ method, url, data: body, ...config });
      data = this.enableCaseConversion && typeof data === 'object' ? convertToCamelCase(data) : data;
      return schema ? this.validateResponse(data, schema) : data;
    } catch (error) {
      // If error is already an ApiError (from interceptor), re-throw it
      if (error instanceof ApiError) {
        throw error;
      }
      // Handle any other errors (e.g., JSON parse errors that bypass the interceptor)
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      throw new ApiError(message, undefined, undefined);
    }
  }

  async get(url: string, params?: Record<string, unknown>, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    return this.request('GET', url, undefined, schema, { ...config, params });
  }

  async post(url: string, body: unknown = {}, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    return this.request('POST', url, body, schema, config);
  }

  async put(url: string, body: unknown = {}, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    return this.request('PUT', url, body, schema, config);
  }

  async patch(url: string, body: unknown = {}, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    return this.request('PATCH', url, body, schema, config);
  }

  async delete(url: string, schema?: TZodSchemaParam, config: AxiosRequestConfig = {}) {
    return this.request('DELETE', url, undefined, schema, config);
  }
}

export default ApiClient;
